import React, { useState } from "react";
import axios from "axios";
import { Navigate, useLocation } from "react-router-dom";
import { KeyRound, LockKeyhole, UserRound } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";

export default function LoginPage() {
  const { status, login } = useAuth();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated") {
    return <Navigate to={location.state?.from || "/"} replace />;
  }

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(username, password);
      setPassword("");
    } catch (requestError) {
      if (axios.isAxiosError(requestError) && requestError.response?.status === 401) {
        setError("Username atau password salah.");
      } else {
        setError(requestError?.response?.data?.message || "Login gagal. Periksa koneksi dan konfigurasi server.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#f5f7f6] px-4 py-10 sm:grid sm:place-items-center">
      <section className="mx-auto w-full max-w-md overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-white shadow-xl shadow-[#0e2b26]/[0.08]">
        <div className="bg-gradient-to-br from-[#0e2b26] to-[#16483d] px-8 py-8 text-white">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[#34d399] text-xl font-black text-[#0e2b26] shadow-lg shadow-black/10">S</div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#a7f3d0]">SILAPDI</p>
          <h1 className="mt-2 text-2xl font-extrabold tracking-tight">Masuk sebagai Admin</h1>
          <p className="mt-2 text-sm text-white/75">Kelurahan Suka Asih · Kota Bandung</p>
        </div>

        <form onSubmit={submit} className="space-y-5 p-8">
          <div>
            <label htmlFor="admin-username" className="mb-2 block text-sm font-semibold text-[#0e2b26]">Username admin</label>
            <div className="relative">
              <UserRound size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                id="admin-username"
                name="username"
                autoComplete="username"
                required
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                className="h-11 w-full rounded-lg border border-[hsl(var(--border))] bg-white pl-10 pr-3 text-sm outline-none transition focus:border-[#0e6b4f] focus:ring-2 focus:ring-[#0e6b4f]/20"
                data-testid="login-username-input"
              />
            </div>
          </div>

          <div>
            <label htmlFor="admin-password" className="mb-2 block text-sm font-semibold text-[#0e2b26]">Password</label>
            <div className="relative">
              <LockKeyhole size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                id="admin-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="h-11 w-full rounded-lg border border-[hsl(var(--border))] bg-white pl-10 pr-3 text-sm outline-none transition focus:border-[#0e6b4f] focus:ring-2 focus:ring-[#0e6b4f]/20"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}

          <button
            type="submit"
            disabled={submitting || status === "loading"}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#0e2b26] text-sm font-bold text-white transition hover:bg-[#16483d] disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="login-submit-button"
          >
            <KeyRound size={16} />
            {submitting ? "Memverifikasi..." : "Masuk"}
          </button>
          <p className="text-center text-xs leading-relaxed text-muted-foreground">Akses hanya untuk administrator yang berwenang.</p>
        </form>
      </section>
    </main>
  );
}
